import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  Visibility,
  Refresh,
  Search,
  FilterList,
  Person,
  Email,
  Phone,
  Language,
  CalendarToday,
  CreditCard,
  History,
  Add,
  Notifications,
  ContentCopy,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { FilterOptions, SortOptions } from '../../types';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface AppUser {
  _id: string;
  firebaseUserId: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
  credits: number;
  deviceTokens: Array<{
    token: string;
    deviceId?: string;
    platform: string;
    addedAt: string;
    lastUsed: string;
  }>;
  metadata: {
    signInMethod: 'google' | 'apple' | 'email';
    lastSignIn?: string;
    totalFaceSwaps: number;
    totalVideoSwaps: number;
    phoneNumber?: string;
    country?: string;
    timezone?: string;
    language?: string;
  };
  security?: {
    isBlocked?: boolean;
    nsfwBypassed?: boolean;
    nsfwForceVision?: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

const AppUsersList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sort, setSort] = useState<SortOptions>({ field: 'createdAt', order: 'desc' });
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [addingCredits, setAddingCredits] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationImageUrl, setNotificationImageUrl] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);

  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'User',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={params.row.photoURL}
            alt={params.row.displayName || params.row.email}
            sx={{ width: 32, height: 32, flexShrink: 0 }}
          >
            {params.row.displayName?.[0] || params.row.email?.[0] || 'U'}
          </Avatar>
          <Typography variant="body2" fontWeight="medium">
            {params.row.displayName || 'No name'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'firebaseUserId',
      headerName: 'Firebase ID',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'signInMethod',
      headerName: 'Sign In',
      width: 120,
      renderCell: (params) => {
        const method = params.row.metadata?.signInMethod || 'google';
        return (
          <Chip
            label={method.charAt(0).toUpperCase() + method.slice(1)}
            size="small"
            color={method === 'google' ? 'primary' : method === 'apple' ? 'default' : 'secondary'}
          />
        );
      },
    },
    {
      field: 'credits',
      headerName: 'Credits',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'totalFaceSwaps',
      headerName: 'Face Swaps',
      width: 110,
      renderCell: (params) => params.row.metadata?.totalFaceSwaps || 0,
    },
    {
      field: 'totalVideoSwaps',
      headerName: 'Video Swaps',
      width: 110,
      renderCell: (params) => params.row.metadata?.totalVideoSwaps || 0,
    },
    {
      field: 'deviceTokens',
      headerName: 'Devices',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value?.length || 0}
          size="small"
          color="info"
        />
      ),
    },
    {
      field: 'lastSignIn',
      headerName: 'Last Sign In',
      width: 140,
      renderCell: (params) => {
        const lastSignIn = params.row.metadata?.lastSignIn;
        return lastSignIn ? format(new Date(lastSignIn), 'MMM dd, yyyy') : 'Never';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 140,
      renderCell: (params) => format(new Date(params.value), 'MMM dd, yyyy'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewUser(params.row)}
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.pageSize, filters, sort]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAppUsers(
        pagination.page + 1,
        pagination.pageSize,
        {
          ...filters,
          sortBy: sort.field,
          sortOrder: sort.order,
        }
      );

      if (response.success && response.data) {
        // Backend returns { users: [], pagination: {} }
        setUsers(response.data.users || []);
        setPagination(prev => ({
          ...prev,
          total: response.data!.pagination?.totalCount || 0,
        }));
      } else {
        setError(response.error || 'Failed to fetch users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: AppUser) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  // Always-on Google Vision NSFW scan for this account. Applies on every device
  // the user signs in on, overriding the global engine toggle and any bypass.
  const [forceVisionBusy, setForceVisionBusy] = useState(false);
  const handleToggleForceVision = async (user: AppUser) => {
    const currentlyForced = user.security?.nsfwForceVision === true;
    setForceVisionBusy(true);
    try {
      if (currentlyForced) {
        await apiService.unforceVisionNsfwOffender({ userId: user._id });
      } else {
        await apiService.forceVisionNsfwOffender({ userId: user._id, reason: 'Admin forced Vision scan (user)' });
      }
      // Reflect the change locally + in the list without a full refetch.
      const next = !currentlyForced;
      setSelectedUser((u) => (u && u._id === user._id ? { ...u, security: { ...u.security, nsfwForceVision: next } } : u));
      setUsers((list) => list.map((u) => (u._id === user._id ? { ...u, security: { ...u.security, nsfwForceVision: next } } : u)));
      toast.success(next ? 'Always-Vision scan enabled for this user' : 'Always-Vision scan removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err.message || 'Failed to update forced Vision');
    } finally {
      setForceVisionBusy(false);
    }
  };

  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSort({
        field: model[0].field,
        order: model[0].sort || 'asc',
      });
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">App Users</Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchUsers}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            size="small"
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            label="Sign In Method"
            size="small"
            value={filters.signInMethod || ''}
            onChange={(e) => handleFilterChange('signInMethod', e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="google">Google</MenuItem>
            <MenuItem value="apple">Apple</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ width: '100%', overflow: 'auto' }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          paginationMode="server"
          paginationModel={{
            page: pagination.page,
            pageSize: pagination.pageSize,
          }}
          rowCount={pagination.total}
          onPaginationModelChange={(model) => {
            setPagination(prev => ({
              ...prev,
              page: model.page,
              pageSize: model.pageSize,
            }));
          }}
          onSortModelChange={handleSortModelChange}
          disableRowSelectionOnClick
          getRowId={(row) => row._id}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          autoHeight
          sx={{
            minWidth: 1800,
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              overflow: 'visible',
            },
            '& .MuiDataGrid-columnHeader': {
              '& .MuiDataGrid-columnSeparator': {
                visibility: 'visible',
                color: 'divider',
                '&:hover': {
                  color: 'primary.main',
                },
              },
            },
          }}
        />
      </Paper>

      {/* View User Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={selectedUser?.photoURL}
              alt={selectedUser?.displayName || selectedUser?.email}
              sx={{ width: 56, height: 56 }}
            >
              {selectedUser?.displayName?.[0] || selectedUser?.email?.[0] || 'U'}
            </Avatar>
            <Box>
              <Typography variant="h6">
                {selectedUser?.displayName || 'No name'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.email || 'No email'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Firebase User ID
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {selectedUser.firebaseUserId}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Sign In Method
                    </Typography>
                    <Chip
                      label={selectedUser.metadata?.signInMethod?.charAt(0).toUpperCase() + selectedUser.metadata?.signInMethod?.slice(1)}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Credits
                    </Typography>
                    <Typography variant="h6">{selectedUser.credits || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Face Swaps
                    </Typography>
                    <Typography variant="h6">{selectedUser.metadata?.totalFaceSwaps || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total Video Swaps
                    </Typography>
                    <Typography variant="h6">{selectedUser.metadata?.totalVideoSwaps || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Device Tokens
                    </Typography>
                    <Typography variant="h6">{selectedUser.deviceTokens?.length || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* NSFW moderation — force always-on Vision for this account */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          NSFW moderation
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Always scan with Google Vision:</Typography>
                          <Chip
                            size="small"
                            color={selectedUser.security?.nsfwForceVision ? 'primary' : 'default'}
                            label={selectedUser.security?.nsfwForceVision ? 'ON' : 'Off'}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Forces every upload from this account to be scanned by Google Vision on any
                          device — overrides the global engine setting and any bypass.
                        </Typography>
                      </Box>
                      <Button
                        variant={selectedUser.security?.nsfwForceVision ? 'outlined' : 'contained'}
                        color="primary"
                        disabled={forceVisionBusy}
                        onClick={() => handleToggleForceVision(selectedUser)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {selectedUser.security?.nsfwForceVision ? 'Turn off' : 'Force Vision scan'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Device Tokens Details
                    </Typography>
                    {selectedUser.deviceTokens && selectedUser.deviceTokens.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        {selectedUser.deviceTokens.map((token, index) => (
                          <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="caption" display="block">
                              <strong>Platform:</strong> {token.platform}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', flex: 1 }}>
                                <strong>Token:</strong> {token.token}
                              </Typography>
                              <Tooltip title="Copy token">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(token.token);
                                    toast.success('Token copied to clipboard');
                                  }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            {token.deviceId && (
                              <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                <strong>Device ID:</strong> {token.deviceId}
                              </Typography>
                            )}
                            <Typography variant="caption" display="block" color="text.secondary">
                              Added: {format(new Date(token.addedAt), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No device tokens</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Sign In
                    </Typography>
                    <Typography variant="body2">
                      {selectedUser.metadata?.lastSignIn
                        ? format(new Date(selectedUser.metadata.lastSignIn), 'MMM dd, yyyy HH:mm')
                        : 'Never'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Joined
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(selectedUser.createdAt), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Notifications />}
            onClick={() => {
              setNotificationDialogOpen(true);
            }}
            variant="contained"
            color="primary"
            disabled={!selectedUser?.deviceTokens || selectedUser.deviceTokens.length === 0}
          >
            Send Push Notification
          </Button>
          <Button
            startIcon={<Add />}
            onClick={() => {
              setAddCreditsDialogOpen(true);
            }}
            variant="contained"
            color="success"
          >
            Add Credits
          </Button>
          <Button
            startIcon={<History />}
            onClick={() => {
              if (selectedUser) {
                setViewDialogOpen(false);
                navigate(`/admin/users/app-users/${selectedUser._id}/transactions`);
              }
            }}
            variant="outlined"
            color="primary"
          >
            View Transaction History
          </Button>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog
        open={addCreditsDialogOpen}
        onClose={() => {
          setAddCreditsDialogOpen(false);
          setCreditsAmount('');
          setCreditsReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Credits to User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                User: {selectedUser.displayName || selectedUser.email || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Credits: {selectedUser.credits || 0}
              </Typography>
              <TextField
                label="Amount"
                type="number"
                fullWidth
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                margin="normal"
                inputProps={{ min: 1, step: 1 }}
                helperText="Enter the number of credits to add"
              />
              <TextField
                label="Reason (Optional)"
                fullWidth
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                placeholder="e.g., Bonus credits, Refund, Promotional credits"
                helperText="Optional reason for adding credits"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddCreditsDialogOpen(false);
              setCreditsAmount('');
              setCreditsReason('');
            }}
            disabled={addingCredits}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!selectedUser || !creditsAmount) return;
              
              const amount = parseInt(creditsAmount);
              if (isNaN(amount) || amount <= 0) {
                toast.error('Please enter a valid positive number');
                return;
              }

              setAddingCredits(true);
              try {
                const response = await apiService.addCreditsToUser(
                  selectedUser._id,
                  amount,
                  creditsReason || undefined
                );

                if (response.success) {
                  toast.success(`Successfully added ${amount} credits to user!`);
                  setAddCreditsDialogOpen(false);
                  setCreditsAmount('');
                  setCreditsReason('');
                  
                  // Refresh user data
                  if (response.data?.user) {
                    setSelectedUser({
                      ...selectedUser,
                      credits: response.data.user.credits
                    });
                  }
                  
                  // Refresh users list
                  fetchUsers();
                } else {
                  toast.error('Failed to add credits: ' + (response.error || 'Unknown error'));
                }
              } catch (error: any) {
                toast.error('Error adding credits: ' + (error.message || 'Unknown error'));
              } finally {
                setAddingCredits(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={addingCredits || !creditsAmount}
            startIcon={addingCredits ? <CircularProgress size={20} /> : <Add />}
          >
            {addingCredits ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Push Notification Dialog */}
      <Dialog
        open={notificationDialogOpen}
        onClose={() => {
          setNotificationDialogOpen(false);
          setNotificationTitle('');
          setNotificationBody('');
          setNotificationImageUrl('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Push Notification</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                User: {selectedUser.displayName || selectedUser.email || 'Unknown'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Devices: {selectedUser.deviceTokens?.length || 0} device(s) will receive this notification
              </Typography>
              <TextField
                label="Title"
                fullWidth
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                margin="normal"
                required
                helperText="Notification title"
              />
              <TextField
                label="Body"
                fullWidth
                value={notificationBody}
                onChange={(e) => setNotificationBody(e.target.value)}
                margin="normal"
                required
                multiline
                rows={3}
                helperText="Notification message"
              />
              <TextField
                label="Image URL (Optional)"
                fullWidth
                value={notificationImageUrl}
                onChange={(e) => setNotificationImageUrl(e.target.value)}
                margin="normal"
                helperText="Optional image URL for the notification"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNotificationDialogOpen(false);
              setNotificationTitle('');
              setNotificationBody('');
              setNotificationImageUrl('');
            }}
            disabled={sendingNotification}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!selectedUser || !notificationTitle || !notificationBody) return;
              
              if (!selectedUser.deviceTokens || selectedUser.deviceTokens.length === 0) {
                toast.error('User has no device tokens');
                return;
              }

              setSendingNotification(true);
              try {
                // Get all FCM tokens from user's device tokens
                const fcmTokens: string[] = selectedUser.deviceTokens
                  .map(dt => dt.token)
                  .filter((token): token is string => token !== undefined && token !== null && token.trim() !== '');

                if (fcmTokens.length === 0) {
                  toast.error('No valid FCM tokens found for this user');
                  setSendingNotification(false);
                  return;
                }

                const notification = {
                  title: notificationTitle.trim(),
                  body: notificationBody.trim(),
                  imageUrl: notificationImageUrl.trim() || undefined,
                };

                const response = await apiService.sendNotificationToFcmTokens(
                  fcmTokens,
                  notification
                );

                if (response.success) {
                  const successCount = response.data?.successCount || fcmTokens.length;
                  const failureCount = response.data?.failureCount || 0;
                  if (failureCount > 0) {
                    toast.success(`Notification sent to ${successCount} device(s), ${failureCount} failed`);
                  } else {
                    toast.success(`Notification sent successfully to ${successCount} device(s)!`);
                  }
                  setNotificationDialogOpen(false);
                  setNotificationTitle('');
                  setNotificationBody('');
                  setNotificationImageUrl('');
                } else {
                  toast.error('Failed to send notification: ' + (response.error || 'Unknown error'));
                }
              } catch (error: any) {
                toast.error('Error sending notification: ' + (error.message || 'Unknown error'));
              } finally {
                setSendingNotification(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={sendingNotification || !notificationTitle || !notificationBody}
            startIcon={sendingNotification ? <CircularProgress size={20} /> : <Notifications />}
          >
            {sendingNotification ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppUsersList;

